import { FileIcon, ImageIcon, MailIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import {
  categorizeDocuments,
  DocumentCategory,
  getDocumentDisplayName,
  type ICategorizedGroup,
  type IViewerDocument,
  type IViewerMetadata,
} from './documentUtils';

interface IDocumentListProps {
  documents: ReadonlyArray<IViewerDocument>;
  metadata?: IViewerMetadata | null;
  selectedDocumentId: string | null;
  onSelectDocument: (doc: IViewerDocument) => void;
}

const CATEGORY_ICONS: Record<DocumentCategory, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  [DocumentCategory.PDF]: FileIcon,
  [DocumentCategory.IMAGE]: ImageIcon,
  [DocumentCategory.EMAIL]: MailIcon,
  [DocumentCategory.OTHER]: FileIcon,
};

const DocumentList = ({ documents, metadata, selectedDocumentId, onSelectDocument }: IDocumentListProps) => {
  const groups = React.useMemo(() => categorizeDocuments(documents), [documents]);

  return (
    <nav className="w-70 flex h-full shrink-0 flex-col overflow-y-auto">
      {metadata && <MetadataSection metadata={metadata} />}
      {groups.map((group) => (
        <DocumentGroup
          key={group.category}
          group={group}
          selectedDocumentId={selectedDocumentId}
          onSelectDocument={onSelectDocument}
        />
      ))}
    </nav>
  );
};

interface IDocumentGroupProps {
  group: ICategorizedGroup;
  selectedDocumentId: string | null;
  onSelectDocument: (doc: IViewerDocument) => void;
}

const DocumentGroup = ({ group, selectedDocumentId, onSelectDocument }: IDocumentGroupProps) => {
  const Icon = CATEGORY_ICONS[group.category];

  return (
    <div className="flex flex-col">
      <div className="text-tertiary flex items-center gap-2 px-4 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide">
        <Icon className="size-3.5 shrink-0" />
        {group.category === DocumentCategory.OTHER
          ? 'Other files'
          : group.category === DocumentCategory.EMAIL
            ? group.category
            : `${group.category}s`}
      </div>
      {group.documents.map((doc, idx) => (
        <DocumentListItem
          key={doc.id}
          doc={doc}
          displayName={getDocumentDisplayName(doc, idx)}
          isSelected={doc.id === selectedDocumentId}
          onSelect={() => onSelectDocument(doc)}
        />
      ))}
    </div>
  );
};

interface IDocumentListItemProps {
  doc: IViewerDocument;
  displayName: string;
  isSelected: boolean;
  onSelect: () => void;
}

const DocumentListItem = ({ doc, displayName, isSelected, onSelect }: IDocumentListItemProps) => (
  <button
    type="button"
    onClick={onSelect}
    className={cx(
      'flex items-center gap-2 px-4 py-2 text-left text-base transition-colors',
      isSelected ? 'bg-active text-primary font-medium' : 'text-secondary hover:bg-tertiary'
    )}
  >
    {doc.previewUrl ? (
      <img src={doc.previewUrl} alt="" className="border-secondary size-8 shrink-0 rounded border object-cover" />
    ) : (
      <div className="bg-tertiary border-secondary flex size-8 shrink-0 items-center justify-center rounded border">
        <FileIcon className="text-quaternary size-4" />
      </div>
    )}
    <span className="truncate">{displayName}</span>
  </button>
);

const MetadataSection = ({ metadata }: { metadata: IViewerMetadata }) => (
  <div className="flex flex-col gap-3 px-4 py-4">
    {metadata.senderIdentifier && <MetadataField label="Person" value={metadata.senderIdentifier} />}
    <MetadataField label="Method" value={metadata.source} />
    {metadata.date && <MetadataField label="Date" value={metadata.date} />}
  </div>
);

const MetadataField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col">
    <span className="text-primary text-sm font-semibold">{label}</span>
    <span className="text-tertiary text-[15px] font-medium">{value}</span>
  </div>
);

export default DocumentList;
